import React, { Component, ErrorInfo, ReactNode } from 'react';

import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, Paper, Typography } from '@mui/material';

import * as amplitude from '@amplitude/analytics-browser';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to analytics
    amplitude.track('JavaScript Error Caught', {
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReload = () => {
    amplitude.track('User Clicked Reload from Error Boundary');
    window.location.reload();
  };

  handleGoHome = () => {
    amplitude.track('User Clicked Go Home from Error Boundary');
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 2,
            bgcolor: '#fafafa',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: '520px',
              maxWidth: '100%',
              borderRadius: '8px',
              p: '32px 50px 40px',
              textAlign: 'center',
            }}
          >
            {/* Error Icon */}
            <Box sx={{ mb: 3 }}>
              <ErrorIcon
                sx={{
                  fontSize: 94,
                  color: '#D32F2F',
                }}
              />
            </Box>

            {/* Error Title */}
            <Typography
              variant='h4'
              sx={{
                mb: 2,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '36px',
                color: '#000000',
              }}
            >
              Something went wrong
            </Typography>

            {/* Error Description */}
            <Typography
              variant='body1'
              sx={{
                mb: 4,
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                lineHeight: '24px',
                color: '#000000',
              }}
            >
              An unexpected error occurred. Please try reloading the page or
              return to the start.
            </Typography>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: '4px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: '#d32f2f',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <strong>Error:</strong> {this.state.error.message}
                <br />
                <strong>Stack:</strong>
                <pre style={{ margin: '8px 0', whiteSpace: 'pre-wrap' }}>
                  {this.state.error.stack}
                </pre>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant='contained'
                onClick={this.handleReload}
                sx={{
                  height: '40px',
                  bgcolor: '#B60207',
                  color: '#FFFFFF',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.75px',
                  '&:hover': {
                    bgcolor: '#B60207',
                    opacity: 0.9,
                  },
                }}
              >
                RELOAD PAGE
              </Button>

              <Button
                variant='text'
                onClick={this.handleGoHome}
                sx={{
                  height: '40px',
                  color: '#B60207',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.75px',
                  '&:hover': {
                    backgroundColor: 'rgba(182, 2, 7, 0.04)',
                  },
                }}
              >
                GO TO START
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
